import {
  ASSETS_FOLDER,
  DefaultRendererType,
  GAME_PROJECT_FILE_EXT,
  PROJECT_PATH_PREFIX,
  SCENE_NAME_EXT
} from '@gnsx/genesys.js';
import enginePackage from '@gnsx/genesys.js/package.json';

import packageJson from '../package.json';

import type {
  RendererType
} from '@gnsx/genesys.js';

import { IS_DEV } from '@/lib/utils';

/** Unsuffixed brand name. Use this wherever the display string "Genesys" appears without an environment suffix. */
export const PRODUCT_NAME = 'Genesys';

export const MESH_COMBINATION_FILE_EXT = '.genesys-mesh-comb';

export const TEXT_ASSET_EXTENSIONS: string[] = [
  SCENE_NAME_EXT,
  GAME_PROJECT_FILE_EXT,
  MESH_COMBINATION_FILE_EXT,
];

export const DEFAULT_SCENE_NAME = `default${SCENE_NAME_EXT}`;
export const PUBLIC_FOLDER: string = 'public';
export const JS_CLASSES_DIR_NAME: string = '@js-classes';
export const DEFAULT_FILE_SERVER_PORT: number = !IS_DEV ? 4000 : 4001;
const defaultFileServerProtocol = process.env.NEXT_PUBLIC_FILE_SERVER_PROTOCOL ?? 'http';
export const DEFAULT_FILE_SERVER_URL: string = process.env.NEXT_PUBLIC_FILE_SERVER_URL ?? `${defaultFileServerProtocol}://localhost:${DEFAULT_FILE_SERVER_PORT}`;
export const SDK_RELEASE_URL: string = 'https://github.com/directivegames/genesys.sdk-release/releases';
export const PROJECT_ASSETS_FOLDER: string = `${PROJECT_PATH_PREFIX}/${ASSETS_FOLDER}`;

export { GAMES_FOLDER } from '@/types/game';

export const EDITOR_VERSION = packageJson.version;
export const ENGINE_VERSION = enginePackage.version;

const toMajorMinorWithZeroPatch = (version: string): string => {
  const [major = '0', minor = '0'] = version.split('.');
  return `${major}.${minor}.0`;
};

/**
 * Lowest SDK version that can still attempt in-app update recovery.
 * Versions below this floor must be updated manually.
 */
export const MINIMUM_SDK_AUTO_UPDATE_VERSION = '10.10.0';
/**
 * Minimum required Genesys SDK version for editor startup.
 * If the installed SDK is below this version, the editor will block loading
 * and display the SdkUpdateWaitingDialog, requiring an update before proceeding.
 */
export const MINIMUM_SDK_VERSION = toMajorMinorWithZeroPatch(ENGINE_VERSION);

/* Editor settings */
export const DEFAULT_RENDERER_TYPE: RendererType = DefaultRendererType;
export const DEFAULT_FOV = 90;
export const DEFAULT_FOCUS_ZOOM = 60;
/** Shared editor viewport camera far plane (perspective and orthographic). */
export const DEFAULT_CAMERA_FAR = 2000;
/** Upper bound for {@link DEFAULT_CAMERA_FAR} (inclusive). */
export const MAX_CAMERA_FAR = 10_000;
export const DEFAULT_OUTLINE_ENABLED = true;
export const DEFAULT_OUTLINE_SHOW_HIDDEN_EDGE = true;
export const DEFAULT_OUTLINE_WIDTH = 1;
export const DEFAULT_OUTLINE_COLOR = '#38BDF8';
/** Default selection outline edge strength (maps to post-process `edgeStrength`). */
export const DEFAULT_OUTLINE_STRENGTH = 2.0;
/** Upper bound for selection outline strength (inclusive). */
export const MAX_OUTLINE_STRENGTH = 5;
/** Post-edge blur for selection outlines (maps to engine `edgeBlur`). */
export const DEFAULT_OUTLINE_EDGE_BLUR = 0.2;

export const DEFAULT_NAVMESH_DEBUG_COLOR = '#22C55E';

export const USE_ISOLATED_PLAY_MODE = true;
export const UNMOUNT_EDITOR_LAYOUT_IN_PLAY_MODE = true;

/**
 * Debug flag: when enabled, skips the editor world creation flow entirely.
 * This allows debugging PIE mode without the editor view being mounted.
 * Set to true for debugging, false for normal editor operation.
 */
export const DEBUG_SKIP_LOAD_EDITOR_WORLD = false;

/**
 * Debug flag: when enabled, skip loading the editor viewport/canvas
 */
export const DEBUG_HIDE_EDITOR_VIEWPORT = false;

/**
 * When enabled, tracks Three.js scenes with WeakRefs and periodic GC logging in the editor viewport.
 * Defaults to development only; set to true to debug scene retention in production builds.
 * When false, no tracking runs (no per-frame work beyond a constant branch).
 */
export const DEBUG_GC_TRACK = IS_DEV && false;

/* ===== Test user configuration for scenario asset filtering ===== */
export const TEST_USERS = {
  USER_A: {
    uid: 'test-user-alice-123',
    email: 'alice@test.com',
    displayName: 'Alice Test'
  },
  USER_B: {
    uid: 'test-user-bob-456',
    email: 'bob@test.com',
    displayName: 'Bob Test'
  },
  USER_C: {
    uid: 'test-user-charlie-789',
    email: 'charlie@test.com',
    displayName: 'Charlie Test'
  }
} as const;

// Set this to override the current user for testing
// Set to null to use real Firebase authentication
// export const TEST_USER_OVERRIDE: keyof typeof TEST_USERS | null = 'USER_A';
export const TEST_USER_OVERRIDE: keyof typeof TEST_USERS | null = null;

/* ===== End Test User Config Section ===== */
